<template>
  <card-canvas class="person-detail">
    <loading-placeholder v-if="isLoading" :lines="10" />
    <template v-else>  
      <card-canvas-two-deep-breadcrumb
        :parent-route="RouteAssistant.getPeopleRoute($route.params.accountId)"
        :child-name="person.name"
        parent-name="Users" />
      <div class="person-detail__header">
        <avatar :person="person" :size="54" />
        <div class="person-info">
          <h2>{{person.name}}</h2>
          <p>{{person.email}}</p>
          <p class="last-active"><span v-if="person.lastActivityDate !== null">Last active:</span> {{person.lastActivityDateDisplay}}</p>
        </div>
        <template v-if="!$screen.isMobile($mq)">
          <more-button-dropdown :force-visibility="showMoreOptions" class="more-dropdown">
            <template #options-list>
              <ul>  
                <li v-if="$permissions.isLoggedInUser(person.id)" @click="RouteAssistant.goToProfile(accountID)">
                  <i class="ddi ddi-profile" />
                  <span>Edit Profile</span>
                </li>  
                <li @click="sendSignInEmail">
                  <i class="ddi ddi-people" />
                  <span> {{ sendInviteText }} </span>
                </li>                 
                <li 
                  v-if="$permissions.canDeleteAccountMembership(person.id, person.accountRoleID)" 
                  class="scary-list-option" 
                  @click="showDeletePersonModal = true"
                >
                  <i class="ddi ddi-trash" />
                  <span>Delete User</span>
                </li>
              </ul>
            </template>
          </more-button-dropdown>
          <div class="actions">
            <single-select v-model="person.companyID" placeholder="Company" @change="updateCompany">
              <el-option v-for="company in companies" :key="company.id" :label="company.name" :value="company.id" />
            </single-select>
            <!-- If able to update account role, only show available roles -->
            <template v-if="$permissions.canUpdateAccountRole(person.id, person.accountRoleID)">
              <single-select v-model="person.accountRoleID" @change="updateAccountRole">
                <el-option v-for="role in availableRoles" :key="role.id" :label="role.name" :value="role.id" />
              </single-select>
            </template>
            <!-- Otherwise, disable, but return all roles to select list so we can guarantee a match -->
            <template v-else>
              <single-select v-model="person.accountRoleID" disabled>
                <el-option v-for="role in AccountRoles" :key="role.id" :label="role.name" :value="role.id" />
              </single-select>
            </template>
          </div>
        </template>
      </div>
      <div class="person-detail__entity-list">
        <template v-if="noAccountProjects && !$screen.isTabletOrMobile($mq)">
          <div class="header">
            <h3>Manage Projects</h3>
          </div>
          <div class="no-entities">
            <router-link :to="RouteAssistant.getCreateProjectRoute($route.params.accountId)" class="just-a-link">
              Create your first project +
            </router-link>
          </div>
        </template>
        <template v-else>
          <div class="header">
            <h3>Manage Projects</h3>
            <dd-button v-if="hasAllProjectsSelected" type="text" class="add-to-all" @click="removeAllProjects">Remove all</dd-button>
            <dd-button v-else type="text" class="add-to-all" @click="selectAllProjects">Add all</dd-button>
          </div>
          <div v-for="project in projects" :key="project.id" class="entities">
            <sample-ribbon v-if="$permissions.isSampleInternalProjectID(project.id)" size="small" />
            <span><i class="ddi ddi-project" /> {{ project.title }}</span>
            <div>
              <avatar-gallery 
                v-if="!$screen.isMobile($mq)"
                :size="avatarGallerySize" 
                :people="getPeopleInEntity(project)" 
                :max-num-avatars="maxNumberAvatars" 
                class="avatar-gallery"
                stack-tightly
              />
              <add-remove-button
                :id="project.id"
                :is-selected="isProjectSelected(project.id)"
                :is-disabled="isSubmitting"
                @unchecked="(id) => removeProject(id)"
                @checked="(id) => addProject(id)"
              />
            </div>
          </div>
        </template>
      </div>
      <template v-if="person.accountRoleID !== 4">
        <div class="person-detail__entity-list">
          <template v-if="noAccountMailboxes && !$screen.isTabletOrMobile($mq)">
            <div class="header">
              <h3>Manage Mailboxes</h3>
            </div>
            <div class="no-entities">
              <router-link :to="RouteAssistant.getCreateMailboxRoute($route.params.accountId)" class="just-a-link">
                Create your first mailbox +
              </router-link>
            </div>
          </template>
          <template v-else>
            <div class="header">
              <h3>Manage Mailboxes</h3>
              <dd-button v-if="hasAllMailboxesSelected" type="text" class="add-to-all" @click="removeAllMailboxes">Remove all</dd-button>
              <dd-button v-else type="text" class="add-to-all" @click="selectAllMailboxes">Add all</dd-button>
            </div>
            <div v-for="mailbox in mailboxes" :key="mailbox.id" class="entities">
              <span><i class="ddi ddi-mailbox" /> {{ mailbox.title }}</span>
              <div>
                <avatar-gallery 
                  v-if="!$screen.isMobile($mq)"
                  :size="avatarGallerySize" 
                  :people="getPeopleInEntity(mailbox)" 
                  :max-num-avatars="maxNumberAvatars"
                  class="avatar-gallery"
                  stack-tightly 
                />
                <add-remove-button
                  :id="mailbox.id"
                  :is-selected="isMailboxSelected(mailbox.id)"
                  :is-disabled="isSubmitting"
                  @unchecked="(id) => removeMailbox(id)"
                  @checked="(id) => addMailbox(id)"
                />
              </div>
            </div>
          </template>
        </div>
      </template>
    </template>
    <!-- Delete person modal -->
    <delete-person-modal 
      v-model:show-modal="showDeletePersonModal" 
      :person="person"
      @person-deleted="RouteAssistant.goToPeople($router, accountID)"
    />
  </card-canvas>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import CardCanvas from '@/layouts/card-canvas.vue'

/* Mixins */

/* Components */
import DeletePersonModal from './components/delete-person-modal.vue'

/* Helpers */
import TypeAssistant from '@/helpers/type-assistant'
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { AccountRoles } from '@/types/defined-types'
import {
  IProjectWithMemberships,
  IPersonWithEmail,
  IAccountMembershipsOutputDetail,
  IItemForSelection,
} from '@/api/types/response.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default defineComponent({
  name: 'PersonDetail',

  components: {
    CardCanvas,
    DeletePersonModal,
  },

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      showMoreOptions: null as any,
      showDeletePersonModal: false,
      maxNumberAvatars: 3,
      avatarGallerySize: 24,
      AccountRoles,
      RouteAssistant,
      rowCount: 1,
      person: {} as IAccountMembershipsOutputDetail,
      mailboxes: [] as IProjectWithMemberships[],
      projects: [] as IProjectWithMemberships[],
      people: [] as IPersonWithEmail[],
      companies: [] as IItemForSelection[],
    }
  },

  head(): any {
    return {
      title: this.person.name,
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    noAccountProjects(): boolean {
      return this.projects.length === 0
    },
    noAccountMailboxes(): boolean {
      return this.mailboxes.length === 0
    },
    accountMembershipID(): number {
      return Number(this.$route.params.accountMembershipId)
    },
    accessGrantedEntityID(): number {
      if (!isNaN(Number(this.$route.query.access_granted))) {
        return Number(this.$route.query.access_granted)
      }
      return null
    },
    hasAllProjectsSelected(): boolean {
      return (
        this.person.listAssignedInternalProjectIDs.length ===
        this.projects.length
      )
    },
    hasAllMailboxesSelected(): boolean {
      return this.person.listAssignedMailboxIDs.length === this.mailboxes.length
    },
    availableRoles(): IItemForSelection[] {
      return this.$permissions.availableRolesToAdmin
    },
    sendInviteText(): string {
      return this.person.hasRegistered
        ? 'Email Password Reset'
        : 'Email Invitation'
    },
  },

  async created() {
    this.isLoading = true
    await Promise.all([
      this.getUpdateOptions(),
      this.getAccountMembershipDetail(),
    ])
    this.checkFromAccessGranted()
    this.isLoading = false
  },

  methods: {
    /* 
       If the incoming route has a querystring param of 'access_granted=[entityID]'
       and if the ID matches a mailbox or project the user is a part of, display
       success messaging. This is for handling redirects from the "Misc.GrantProjectAccess"
       route on the .NET backend for granting mailbox/internal project access requests
       so the granting user can see the update is made.
    */
    checkFromAccessGranted() {
      if (
        this.accessGrantedEntityID &&
        this.getEntityNameIfSelected(this.accessGrantedEntityID)
      ) {
        this.$messaging.showSuccess(
          this.person.name +
            ' is now a member of ' +
            this.getEntityNameIfSelected(this.accessGrantedEntityID),
        )
      }
    },
    addRow(): void {
      this.rowCount++
    },
    getEntityNameIfSelected(id: number): string {
      if (this.isProjectSelected(this.accessGrantedEntityID)) {
        return this.projects.find(p => p.id === id).title
      }
      if (this.isMailboxSelected(this.accessGrantedEntityID)) {
        return this.mailboxes.find(m => m.id === id).title
      }
      return null
    },
    isProjectSelected(id: number): boolean {
      return this.person.listAssignedInternalProjectIDs.includes(id)
    },
    isMailboxSelected(id: number): boolean {
      return this.person.listAssignedMailboxIDs.includes(id)
    },
    getPeopleInEntity(entity: IProjectWithMemberships): IPersonWithEmail[] {
      return this.people.filter(p => entity.listUserIDs.includes(p.id))
    },
    closeOptions() {
      this.showMoreOptions = true
      this.$nextTick(() => {
        this.showMoreOptions = false
      })
    },
    async getUpdateOptions() {
      try {
        const updateOptions = await AccountMembershipsAPI.get_UpdateOptions()
        this.mailboxes = updateOptions.listMailboxes
        this.projects = updateOptions.listInternalProjects
        this.companies = updateOptions.listCompanies
        this.people = updateOptions.listPeople
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async getAccountMembershipDetail() {
      try {
        this.person = await AccountMembershipsAPI.get_Detail(
          this.accountMembershipID,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async sendSignInEmail() {
      try {
        const response = await AccountMembershipsAPI.get_SendSignInEmail(
          this.accountMembershipID,
        )
        this.$messaging.showSuccess(response.message)
        this.closeOptions()
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async addProject(id: number) {
      this.person.listAssignedInternalProjectIDs.push(id)
      this.updateEntityMemberships()
    },
    async removeProject(id: number) {
      this.person.listAssignedInternalProjectIDs = this.person.listAssignedInternalProjectIDs.filter(
        p => p !== id,
      )
      this.updateEntityMemberships()
    },
    async addMailbox(id: number) {
      this.person.listAssignedMailboxIDs.push(id)
      this.updateEntityMemberships()
    },
    async removeMailbox(id: number) {
      this.person.listAssignedMailboxIDs = this.person.listAssignedMailboxIDs.filter(
        m => m !== id,
      )
      this.updateEntityMemberships()
    },
    async selectAllProjects() {
      this.person.listAssignedInternalProjectIDs = this.projects.map(p => p.id)
      this.updateEntityMemberships()
    },
    async removeAllProjects() {
      this.person.listAssignedInternalProjectIDs = []
      this.updateEntityMemberships()
    },
    async selectAllMailboxes() {
      this.person.listAssignedMailboxIDs = this.mailboxes.map(p => p.id)
      this.updateEntityMemberships()
    },
    async removeAllMailboxes() {
      this.person.listAssignedMailboxIDs = []
      this.updateEntityMemberships()
    },
    async updateEntityMemberships() {
      this.isSubmitting = true

      const entityIDs = [
        ...this.person.listAssignedMailboxIDs,
        ...this.person.listAssignedInternalProjectIDs,
      ]

      try {
        const response = await AccountMembershipsAPI.put_UpdateProjectMemberships(
          this.accountMembershipID,
          entityIDs,
        )
        /* Regrab the update options to update mailbox/project lists to update counts */
        await this.getUpdateOptions()
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAccountMembershipDetail()
      }
      this.isSubmitting = false
    },

    async updateCompany() {
      try {
        const response = await AccountMembershipsAPI.put_UpdateCompany(
          this.accountMembershipID,
          this.person.companyID,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async updateAccountRole() {
      try {
        const response = await AccountMembershipsAPI.put_UpdateRole(
          this.accountMembershipID,
          this.person.accountRoleID,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAccountMembershipDetail()
      }
    },
  },
})
</script>

<style lang="scss">
.person-detail {
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    > .person-info {
      padding-left: 12px;

      > h2 {
        max-width: 350px;
        margin-bottom: 0;
        line-height: 1.2;
        @include single-line-ellipsis;
      }

      > p {
        color: var(--color-text-third);
      }

      > .last-active {
        @include lettering--p-sm--no-color;
      }
    }

    > .more-dropdown {
      position: absolute;
      top: 30px;
      right: 30px;
    }

    > .actions {
      display: flex;
      margin-left: auto;

      > * {
        margin-left: 8px;
      }
    }
  }

  &__entity-list {
    padding-bottom: 20px;

    > .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: solid 2px $color--gray-15;
      width: calc(100% + 72px);
      left: -36px;
      position: relative;
      padding: 2px 36px 4px 36px;

      > h3 {
        margin: 0;

        // Project / mailbox icons
        > .ddi {
          font-size: 18px;
          color: $color--gray-60 !important;
          top: -2px;
          left: 8px;
        }
      }

      > .add-to-all {
        left: 20px;
        position: relative;
      }

      @include media-breakpoint-down(sm) {
        width: calc(100% + 40px);
        left: -20px;
        padding: 2px 20px;
      }
    }

    > .no-entities {
      padding: 20px 0;
      @include lettering--p;
    }

    > .entities {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 36px;
      border-bottom: $border--solid-extra-light;
      @include lettering--p;
      width: calc(100% + 72px);
      left: -36px;
      position: relative;

      > span {
        font-weight: $bold;
        @include single-line-ellipsis;
      }

      .sample-ribbon {
        top: 14px;
        left: 0;
        @include media-breakpoint-up(md) {
          left: -48px;
        }
      }

      .ddi-project,
      .ddi-mailbox {
        padding-right: 6px;
        color: var(--color-text-third);
      }

      > div {
        display: flex;
        align-items: center;
        justify-content: space-between;

        @include media-breakpoint-down(sm) {
          justify-content: flex-end;
        }

        @include media-breakpoint-up(md) {
          width: 160px;
        }
      }

      @include media-breakpoint-up(md) {
        &:hover {
          background-color: var(--color-background-blue-15);
        }
      }

      @include media-breakpoint-down(sm) {
        width: calc(100% + 40px);
        left: -20px;
        padding: 8px 20px;
      }
    }
  }
}
</style>
